import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../shared/context/auth-context';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { useDetectUserwayBiggerText } from '../../shared/hooks/useDetectUserwayBiggerText';
import { FriendAndFamily, FriendAndFamilyActiveProfile } from '../../types/models/user';
import { InviteLeftMenu } from './InviteLeftMenu';
import './Invite.css';
import { ManageFnFRightMenu } from './ManageFnFRightMenu';

type InviteProps = {
  hide: boolean;
};

export function Invite({ hide }: InviteProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isBiggerText } = useDetectUserwayBiggerText();
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const [userFnF, setUserFnF] = useState<FriendAndFamily[] | undefined>(undefined);
  const [activeProfile, setActiveProfile] = useState<FriendAndFamilyActiveProfile | undefined>(
    undefined
  );
  const [showRightMenu, setShowRightMenu] = useState<boolean>(false);
  const [showLeftMenu, setShowLeftMenu] = useState<boolean>(true);

  useEffect(() => {
    if (hide) {
      setShowRightMenu(false);
      setShowLeftMenu(true);
    } else {
      setShowRightMenu(true);
      setShowLeftMenu(true);
    }
  }, [hide]);

  useEffect(() => {
    // F&F and Hub users should not be able to access this page
    if (auth.isHub || auth.access === '2') {
      navigate('/for-you');
    }
  }, [auth]);

  const getProfile = async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/profile/friendsAndFamily`
      );
      if (responseData.fandf) {
        setUserFnF(responseData.fandf);
      }
    } catch (err) {}
  };
  useEffect(() => {
    if (!userFnF && auth.userId) {
      getProfile();
    }
  }, [auth.userId, userFnF]);

  useEffect(() => {
    if (!activeProfile && userFnF?.length === 0) {
      setActiveProfile(() => ({
        id: 0,
        action: 'invite',
        user: null,
      }));
    } else if (!activeProfile && userFnF?.length > 0) {
      setActiveProfile(() => ({
        id: 0,
        action: 'manage',
        user: userFnF[0],
      }));
    } else if (activeProfile && userFnF?.[activeProfile.id]?.name) {
      setActiveProfile((prevState) => ({
        ...prevState,
        action: 'manage',
        user: userFnF[activeProfile.id],
      }));
    } else if (activeProfile && !userFnF?.[activeProfile.id]?.name) {
      setActiveProfile((prevState) => ({
        ...prevState,
        action: 'invite',
        user: null,
      }));
    }
  }, [userFnF]);

  const handleActiveProfile = (index) => {
    if (hide) {
      setShowLeftMenu(false);
      setShowRightMenu(true);
    }
    if (index < userFnF?.length) {
      setActiveProfile({
        id: index,
        action: 'manage',
        user: userFnF[index],
      });
    } else {
      setActiveProfile({
        id: index,
        action: 'invite',
        user: null,
      });
    }
  };

  const saveChanges = async (profile) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/profile/friendsAndFamily`,
        'PUT',
        JSON.stringify({
          userObj: activeProfile.user,
          newName: profile.name,
          newColour: profile.colour,
        })
      );
    } catch (err) {}
    getProfile();
  };

  const removeUser = async (user) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/profile/friendsAndFamily`,
        'DELETE',
        JSON.stringify({
          name: user.name,
          status: user.status,
        })
      );
    } catch (err) {}
    getProfile();
    goBack();
  };

  const generateLink = async (name, colour) => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/profile/friendsAndFamily`,
        'POST',
        JSON.stringify({
          name,
          colour,
        })
      );
      if (responseData) {
        getProfile();
      }
    } catch (err) {}
  };

  const goBack = () => {
    if (hide) {
      setShowLeftMenu(true);
      setShowRightMenu(false);
    }
  };

  return (
    <div className="profile-container">
      {activeProfile && showLeftMenu && (
        <div
          className={`menu-left ${hide ? 'simplify' : ''} ${isBiggerText ? 'small-padding' : ''}`}
          role="group"
          aria-label={t('profile.invite.title')}
        >
          <InviteLeftMenu
            userFnF={userFnF}
            activeProfile={activeProfile}
            handleActiveProfile={handleActiveProfile}
          />
        </div>
      )}
      {activeProfile && showRightMenu && (
        <div
          className={`menu-right ${hide ? 'simplify' : ''}`}
          role="group"
          aria-label={t('profile.invite.manage')}
        >
          <ManageFnFRightMenu
            userFnF={userFnF}
            profile={activeProfile}
            saveChanges={saveChanges}
            removeUser={removeUser}
            generateLink={generateLink}
            hide={hide}
            goBack={goBack}
          />
        </div>
      )}
    </div>
  );
}
